package uk.co.nickthecoder.kyd

import uk.co.nickthecoder.glok.application.Application
import uk.co.nickthecoder.glok.property.asReadOnly
import uk.co.nickthecoder.glok.property.property
import uk.co.nickthecoder.glok.util.log
import uk.co.nickthecoder.kojak.Json
import uk.co.nickthecoder.kojak.JsonObject
import uk.co.nickthecoder.kyd.json.createActFromJson
import uk.co.nickthecoder.kyd.nodes.PlayActView
import uk.co.nickthecoder.kyd.util.Ticker

/**
 * [Management] exists the entire time that the program is running, and is in charge of the boring nitty-gritty,
 * such as loading [Act]s.
 *
 * There is only ever 1 instance of this class. You may create a subclass and then set it on the "root" (Game)
 * resource. Many / most games work fine using this default implementation.
 *
 * [Management] should not be confused with [Director]. A [Director] is in charge of a single [Act], and is no
 * longer used when the [Act] finishes.
 */
open class Management(

    val game: Game

) {

    protected val mutableActResourceProperty by property<ActResource?>(null)

    val actResourceProperty = mutableActResourceProperty.asReadOnly()
    var actResource by mutableActResourceProperty
        protected set

    protected val mutableActProperty by property<Act?>(null)

    val actProperty = mutableActProperty.asReadOnly()
    var act by mutableActProperty
        protected set


    init {
        Application.idleFramePeriod = 1000 / 60

        actProperty.addChangeListener { _, oldAct, newAct -> newAct?.let { onActChanged(oldAct, newAct) } }
    }

    open fun preTick(seconds: Float) {}
    open fun postTick(seconds: Float) {}

    open fun positionSnapping(stage: Stage): Snapping = NoSnapping

    open fun terminate() {
        act?.stop()
        PlayActView.instance.scene?.stage?.close()
    }

    open fun reloadAct() {
        actResource?.let {
            loadAct(it.path())
        }
    }

    /**
     * @param path The path to an [ActResource]. If this is a relative path, then it is relative to the
     * current [actResource] (or root if this is the very first act to be loaded and therefore [actResource] == `null`).
     */
    open fun loadAct(path: String) {

        act?.let { oldAct ->
            oldAct.stop()
            onActEnded(oldAct)
        }

        fun loadComplete(act: Act, actResource: ActResource) {
            this.actResource = actResource
            this.act = act
            act.start()
        }

        val actResource = (actResource ?: game).find(path) as? ActResource
        if (actResource == null) {
            log.error("Act not found '$path'")
            return
        }

        game.resources.loadText(actResource.path() + ".act") { actText ->

            val jAct = Json.parse(actText) as JsonObject
            //this.actResource = actResource
            val loadedAct = createActFromJson(game, jAct)

            if (actResource.includeActPaths.isEmpty()) {

                loadComplete(loadedAct, actResource)

            } else {
                val includeActResources = actResource.includeActPaths.mapNotNull { actResource.find(it) }
                var counter = includeActResources.size
                for (includeActResource in includeActResources) {
                    game.resources.loadText(includeActResource.path() + ".act") { includeActText ->

                        val jIncludeAct = Json.parse(includeActText) as JsonObject
                        val includeAct = createActFromJson(game, jIncludeAct)

                        loadedAct.merge(includeAct)
                        counter --
                        if (counter == 0) {
                            loadComplete(loadedAct, actResource)
                        }
                    }
                }
            }
        }
    }

    protected open fun onActChanged(oldAct: Act?, act: Act) {
        onActLoaded(act)
        onActStarted(act)
    }

    protected open fun onActEnded(act: Act) {
        act.director.onActEnded(act)
    }

    open protected fun onActLoaded(act: Act) {
        //println("Finished loading the act")
        for (stage in act.stages) {
            stage.playing = true
        }
        for (stage in act.stages) {
            for (actor in stage.actors.toList()) {
                actor.behaviour.onEnteredStage(actor)
            }
        }
    }

    open protected fun onActStarted(act: Act) {
        act.director.onActStarting(act)

        for (stage in act.stages) {
            for (actor in stage.actors.toList()) {
                try {
                    actor.behaviour.onActStarted(actor)
                } catch (e: Exception) {
                    log.severe("Behaviour ${actor.behaviour} threw during onActStarted")
                    log.severe(e)
                }
            }
        }

        act.director.onActStarted(act)
    }

    companion object {

        lateinit var instance: Management

        fun loadAct(path: String) = instance.loadAct(path)
        fun terminate() = instance.terminate()

    }

}
